import { useState } from "react";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { PillButton } from "./PillButton";

export const homepageFaqs = [
  {
    q: "Is feedback really 100% anonymous?",
    a: "Yes. Database-level triggers strip every byte of PII (email, IP, name, role) before insert. Even our own platform owners can't identify a respondent.",
  },
  {
    q: "How is this different from Officevibe, Culture Amp, or Lattice?",
    a: "Those are survey platforms with anonymous mode bolted on. SendFeedback.ai is anonymous-first: PII is stripped at the database, AI runs the conversation, and you get themes and risk scoring without manual tagging.",
  },
  {
    q: "What about works councils and EU compliance?",
    a: "We're GDPR-compliant with EU data residency and a signed DPA on request. Because no PII is ever stored, works council reviews are typically straightforward — we have a template document available.",
  },
  {
    q: "How long does setup take?",
    a: "Most teams launch in under 5 minutes. Pick a plan, create a feedback page, share the link — that's it. No IT, no integrations required to start.",
  },
  {
    q: "Can I run pulse campaigns and one-off surveys?",
    a: "Both. Run continuous always-on feedback channels, scheduled pulses, or one-time campaigns — all anonymous, all in one inbox.",
  },
  {
    q: "Do you integrate with Slack, HRIS, or SSO?",
    a: "Yes — Slack notifications and HRIS integrations on Business+. SSO/SAML on Enterprise. Webhooks available on every paid plan.",
  },
  {
    q: "What happens if I exceed my plan's limits?",
    a: "Nothing breaks. We notify you and you can upgrade in one click. We never lock you out of feedback you've already received.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. No annual lock-in unless you opt for the yearly discount. Cancel from settings — keeps your data accessible for 30 days.",
  },
];

export const FAQAccordion = () => {
  const [open, setOpen] = useState<number | null>(0);
  const navigate = useNavigate();

  return (
    <section className="container mx-auto px-3 lg:px-8 py-12 sm:py-16 lg:py-24" id="faq">
      <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-8 lg:gap-16 items-start">
        {/* Left: editorial title */}
        <div className="lg:sticky lg:top-28 text-center lg:text-left">
          <h2 className="text-3xl sm:text-5xl lg:text-[56px] tracking-[-0.02em] leading-[1.05]">
            Frequently <span className="font-display">Asked</span> Questions
          </h2>
          <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-sm mx-auto lg:mx-0">
            Can't find your answer? Our help center covers setup, anonymity, and compliance in detail.
          </p>
          <div className="mt-6 flex justify-center lg:justify-start">
            <PillButton variant="outline" size="md" onClick={() => navigate("/help")}>
              Go To Help Center
            </PillButton>
          </div>
        </div>

        {/* Right: hairline accordion */}
        <div className="border-t border-border">
          {homepageFaqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} className="border-b border-border">
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-start justify-between gap-4 sm:gap-6 py-5 sm:py-6 text-left min-h-[56px] group"
                >
                  <span className="text-[16px] sm:text-lg lg:text-xl text-foreground leading-snug font-medium group-hover:opacity-70 transition-opacity">
                    {f.q}
                  </span>
                  <span
                    className={cn(
                      "relative mt-1 h-5 w-5 shrink-0 text-foreground/60 transition-transform duration-200",
                      isOpen && "rotate-45"
                    )}
                    aria-hidden
                  >
                    <span className="absolute left-0 top-1/2 h-[1.5px] w-5 -translate-y-1/2 bg-current" />
                    <span className="absolute top-0 left-1/2 h-5 w-[1.5px] -translate-x-1/2 bg-current" />
                  </span>
                </button>
                {isOpen && (
                  <div className="pb-6 pr-8 text-sm sm:text-base text-muted-foreground leading-relaxed animate-fade-in">
                    {f.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

